import { useEffect, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { XClose } from "@untitledui/icons"
import { toast } from "sonner"

import { useBulkMessage } from "@/lib/api/use-ghl"
import { cx } from "@/lib/utils/cx"
import { DARK_BTN, pill } from "./shared"

// Send an offer — one text to the people in front of you. Skips anyone
// without a number or who opted out, and says so before you send.

const TEMPLATES = [
  { key: "winback", label: "Win-back", body: "Hi {first}, your estimate at LaFontaine Collision is still good this month — book a drop-off and we'll include a free wash." },
  { key: "check", label: "Free check", body: "Hi {first}, we're offering free damage checks this week. Want us to hold a slot for you?" },
  { key: "review", label: "Review ask", body: "Hi {first}, thanks for trusting LaFontaine Collision. A quick review would mean a lot: lafontaine.link/review" },
]

export interface OfferRecipient { id: string; firstName: string; phone: string | null; smsBlocked: boolean }

export function OfferDialog({ open, teamId, recipients, onClose }: { open: boolean; teamId: number | string; recipients: OfferRecipient[]; onClose: () => void }) {
  const reduce = useReducedMotion()
  const send = useBulkMessage(teamId)
  const [tpl, setTpl] = useState(TEMPLATES[0].key)
  const [body, setBody] = useState(TEMPLATES[0].body)
  useEffect(() => { if (open) { setTpl(TEMPLATES[0].key); setBody(TEMPLATES[0].body) } }, [open])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  const reachable = recipients.filter((r) => r.phone && !r.smsBlocked)
  const skipped = recipients.length - reachable.length

  const submit = async () => {
    if (!body.trim() || reachable.length === 0) return
    try {
      const r = await send.mutateAsync({ contactIds: reachable.map((x) => x.id), body: body.trim() })
      toast.success(`Sent to ${r.sent}${r.skippedConsent + r.skippedNoContact ? ` · ${r.skippedConsent + r.skippedNoContact} skipped` : ""}`)
      onClose()
    } catch {
      toast.error("Couldn't send — try again")
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-end justify-center bg-zinc-900/30 p-0 sm:items-center sm:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }} onClick={onClose}>
          <motion.div
            role="dialog" aria-modal="true" aria-label="Send an offer"
            initial={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(12px) scale(0.98)" }}
            animate={{ opacity: 1, transform: "translateY(0px) scale(1)" }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, transform: "translateY(8px) scale(0.98)" }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full rounded-t-2xl bg-white p-5 shadow-xl ring-1 ring-zinc-900/10 sm:max-w-[560px] sm:rounded-2xl"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-[16px] font-semibold text-zinc-900">Send an offer</h2>
                <p className="mt-0.5 text-[12.5px] text-zinc-500">
                  To {reachable.length} {reachable.length === 1 ? "person" : "people"}{skipped ? ` · ${skipped} skipped (no number or opted out)` : ""}. "Reply STOP to opt out" is added.
                </p>
              </div>
              <button type="button" onClick={onClose} className="grid size-9 shrink-0 place-items-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900" aria-label="Close"><XClose className="size-4" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5" role="radiogroup" aria-label="Template">
              {TEMPLATES.map((t) => (
                <button key={t.key} type="button" role="radio" aria-checked={tpl === t.key} onClick={() => { setTpl(t.key); setBody(t.body) }} className={pill(tpl === t.key)}>{t.label}</button>
              ))}
            </div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} aria-label="Message" className="mt-3 w-full resize-none rounded-lg border border-zinc-300 px-3 py-2 text-[14px] text-zinc-900 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10" />
            <p className="mt-1 text-[12px] text-zinc-400">{"{first}"} becomes their first name.</p>
            <div className="mt-3 flex justify-end">
              <button type="button" onClick={() => void submit()} disabled={!body.trim() || reachable.length === 0 || send.isPending} className={cx(DARK_BTN)}>{send.isPending ? "Sending…" : `Send to ${reachable.length}`}</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
